import type { ModalCopyType } from "@circle-react-shared/TrixEditor/InlineModalSelector/ModalCopy";
import { modalCopy } from "@circle-react-shared/TrixEditor/InlineModalSelector/ModalCopy";
import type { DropzoneFile } from "@circleco/compass/components/Dropzone";
import { Modal } from "@circleco/compass/components/Modal";
import { SegmentedControl } from "@circleco/compass/components/SegmentedControl";
import type { RefObject } from "react";
import { useState } from "react";

import { t } from "@/i18n-js/instance";

import {
  ACCEPT_BY_ATTRIBUTE,
  EMBED_ATTRIBUTE,
  EMBED_FORM_ID,
  FILE_ATTRIBUTES,
  FIND_ONLINE_FORM_ID,
  IMAGE_ATTRIBUTE,
  TAB_VALUES,
  type TabValue,
} from "./constants";
import { EmbedPanel } from "./EmbedPanel";
import { FindOnlinePanel } from "./FindOnlinePanel";
import { getEmbedDescription, isProviderSubtype } from "./getEmbedDescription";
import { UploadPanel } from "./UploadPanel";

const MEDIA_TITLE_KEYS: Partial<Record<ModalCopyType, string>> = {
  image: "attach_image",
  video: "attach_video",
  audio: "attach_audio",
  pdf: "attach_pdf",
  file: "attach_files",
};

export interface EditorAttachmentModalProps {
  attribute?: string;
  finalFocus?: RefObject<HTMLElement | null>;
  insertFile: (file: File) => void;
  onOpenChange: (open: boolean) => void;
  onSubmitUrl: (url: string) => void | Promise<void>;
  open: boolean;
  subtype?: ModalCopyType;
}

export const EditorAttachmentModalComponent = ({
  attribute,
  finalFocus,
  insertFile,
  onOpenChange,
  onSubmitUrl,
  open,
  subtype,
}: EditorAttachmentModalProps) => {
  const [selectedTab, setSelectedTab] = useState<TabValue>(TAB_VALUES.upload);
  const [files, setFiles] = useState<DropzoneFile[]>([]);

  const copy = modalCopy(subtype);
  const isImage = attribute === IMAGE_ATTRIBUTE;
  const isEmbed = attribute === EMBED_ATTRIBUTE;

  if (!isImage && !FILE_ATTRIBUTES.includes(attribute ?? "") && !isEmbed) {
    return null;
  }

  const handleClose = () => onOpenChange(false);

  const localFiles = files.flatMap(({ file }) => (file ? [file] : []));

  const handleSaveFiles = () => {
    if (localFiles.length === 0) return;
    handleClose();
    localFiles.forEach(insertFile);
  };

  const getTitle = () => {
    if (!isEmbed) {
      const titleKey = subtype && MEDIA_TITLE_KEYS[subtype];
      return titleKey ? t(`trix_editor.modal_copy.${titleKey}`) : copy.itemName;
    }
    if (isProviderSubtype(subtype)) {
      return t("tiptap.extensions.embed.add_provider", {
        provider: copy.itemName,
      });
    }
    return t("tiptap.extensions.embed.add");
  };

  const embedDescription = getEmbedDescription(subtype, copy);

  const segmentOptions = [
    { value: TAB_VALUES.upload, label: t("upload") },
    isImage
      ? {
          value: TAB_VALUES.findOnline,
          label: t("trix_editor.modal_copy.find_online"),
        }
      : {
          value: TAB_VALUES.embed,
          label: t("trix_editor.modal_copy.embed_link"),
        },
  ];

  const getPrimaryAction = () => {
    if (isEmbed || selectedTab === TAB_VALUES.embed) {
      return {
        label: copy.embedBtn,
        type: "submit" as const,
        form: EMBED_FORM_ID,
      };
    }
    if (selectedTab === TAB_VALUES.findOnline) {
      return {
        label: t("save"),
        type: "submit" as const,
        form: FIND_ONLINE_FORM_ID,
      };
    }
    return {
      label: t("save"),
      disabled: localFiles.length === 0,
      onClick: handleSaveFiles,
    };
  };

  return (
    <Modal.Root
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) handleClose();
      }}
      size={isImage ? "lg" : "md"}
      finalFocus={finalFocus}
    >
      <Modal.Content>
        <Modal.Header title={getTitle()} />
        <Modal.Body>
          {isEmbed ? (
            <EmbedPanel
              copy={copy}
              description={embedDescription}
              onClose={handleClose}
              onSubmitUrl={onSubmitUrl}
            />
          ) : (
            <div className="flex flex-col gap-4">
              <SegmentedControl
                fullWidth
                required
                options={segmentOptions}
                value={selectedTab}
                onValueChange={value => setSelectedTab(value as TabValue)}
              />
              {selectedTab === TAB_VALUES.upload && (
                <UploadPanel
                  accept={ACCEPT_BY_ATTRIBUTE[attribute ?? ""]}
                  copy={copy}
                  files={files}
                  onFilesChange={setFiles}
                />
              )}
              {selectedTab === TAB_VALUES.embed && (
                <EmbedPanel
                  copy={copy}
                  description={embedDescription}
                  onClose={handleClose}
                  onSubmitUrl={onSubmitUrl}
                />
              )}
              {isImage && selectedTab === TAB_VALUES.findOnline && (
                <FindOnlinePanel
                  copy={copy}
                  insertFile={insertFile}
                  onClose={handleClose}
                  onSubmitUrl={onSubmitUrl}
                />
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{ label: t("cancel") }}
          primaryAction={getPrimaryAction()}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
